import cn from 'classnames';
import { getHasRentalProduct, hasStockStatusCreateItems } from 'components/Cart/selector';
import connect from 'components/Shared/connect';
import Spinner from 'components/Shared/Loader';
import Price from 'components/Shared/Price';
import { CheckoutPageModel } from 'models/CheckoutPageModel';
import { ICart } from 'models/ICart';
import { LoadingStatus } from 'models/LoadingStatus';
import { SiteMode } from 'models/SiteMode';
import { useSelector } from 'react-redux';
import { rm } from 'utils';
import { getVoucherTotal } from 'views/CheckoutPage/selector';
import styles from './styles.scss';
import { selectIsValidated } from 'store/appShell/selector';
import { containsZipDependantItem } from './selector';
import { Summary } from 'models/Summary';
import { SummaryItem } from 'models/SummaryItem';
import { SummaryItemList } from './summaryItemList';

interface Props extends Pick<ICart, 'vouchers'> {
  isLoading: boolean;
  hasStockStatusCreateItems: boolean;
  showFreightMissing: boolean;
  summary: Summary;
  voucherTotal: number;
  total: number;
  totalIncludingVat: number;
  selectedSiteMode: SiteMode;
}

interface NoteProps {
  isVisible: boolean;
  text: string;
}
const Note = ({ isVisible, text }: NoteProps): JSX.Element | null => {
  if (!isVisible) return null;

  return (
    <div className={styles.info}>
      <div className={styles.manufacture_dot} />
      <span>{text}</span>
    </div>
  );
};

const _PriceSummary = ({
  isLoading,
  vouchers,
  showFreightMissing,
  hasStockStatusCreateItems,
  ...totalProps
}: Props) => {
  const hasRentalProducts = useSelector(getHasRentalProduct);
  const isValidated = useSelector(selectIsValidated);

  return (
    <div className={styles.priceSummary}>
      <div className={styles.infoContainer}>
        <Note isVisible={hasRentalProducts} text={rm('cart_rental_text')} />
        <Note isVisible={hasStockStatusCreateItems} text={rm('cart_norefundtext_rd3')} />
      </div>
      <div className={styles.price}>
        <SummaryItemList />
        {!isValidated && <Vouchers vouchers={vouchers} />}
        {showFreightMissing && (
          <span className={cn(styles.priceRow, styles.freightWillCalculate)}>
            {rm('checkout_freight_missing_details_rd3')}
          </span>
        )}
        {isLoading && <Spinner className={styles.spinner} loading />}
      </div>
      <Total {...totalProps} />
    </div>
  );
};

const Vouchers = ({ vouchers }: Pick<Props, 'vouchers'>) => (
  <>
    {vouchers.map(({ id, amount }) => (
      <span key={id} className={styles.priceRow}>
        <span>{rm('checkout_giftcard')}</span>
        <Price price={-amount} appearance="discount" />
      </span>
    ))}
  </>
);

const Total = ({
  total,
  totalIncludingVat,
  voucherTotal,
  selectedSiteMode
}: Pick<Props, 'total' | 'totalIncludingVat' | 'voucherTotal' | 'selectedSiteMode'>) => {
  const isCompany = selectedSiteMode === SiteMode.Company;
  const amountToPay = (isCompany ? total : totalIncludingVat) - voucherTotal;
  const totalText = isCompany ? rm('checkout_total_excluding_vat') : rm('checkout_total');

  return (
    <div className={cn(styles.priceRow, styles.total)}>
      <span className={styles.total_text}>
        <StyleFirstWord text={totalText} className={cn(styles.bold, styles.upperCase)} />
      </span>
      <Price
        price={amountToPay}
        appearance="discount"
        className={cn(styles.total_price, styles.bold)}
      />
    </div>
  );
};

interface StyleFirstWordProps {
  text: string;
  className: string;
}
const StyleFirstWord = ({ text, className }: StyleFirstWordProps) => {
  const [first, ...words] = text.split(' ');

  return (
    <span>
      <span className={className}>{first}</span>
      &nbsp;
      {words.join(' ')}
    </span>
  );
};

interface StateProps {
  _summaryItems: SummaryItem[];
  cartStatus: LoadingStatus;
  pageStatus: LoadingStatus;
}
export const PriceSummaryBob = connect<Props, Record<string, unknown>, StateProps>(
  state => ({
    cartStatus: state.cart.status,
    pageStatus: state.currentPage.status,
    hasStockStatusCreateItems: hasStockStatusCreateItems(state),
    _summaryItems: (state.currentPage as CheckoutPageModel).summary.items,
    vouchers: state.cart.vouchers,
    total: (state.currentPage as CheckoutPageModel).summary.total,
    totalIncludingVat: (state.currentPage as CheckoutPageModel).summary.totalIncludingVat,
    selectedSiteMode: state.appShell.siteMode
  }),
  undefined,
  ({ cartStatus, pageStatus, _summaryItems, vouchers, ...state }, dispatch, props) => ({
    ...state,
    ...dispatch,
    ...props,
    isLoading: cartStatus === LoadingStatus.Fetching || pageStatus === LoadingStatus.Fetching,
    vouchers,
    voucherTotal: getVoucherTotal(vouchers),
    showFreightMissing: _summaryItems ? _summaryItems.some(containsZipDependantItem) : false
  })
)(_PriceSummary);
